/**
 * @file        src/lib/seoHead.js
 * @description The storefront's head manager: pure DOM functions that write a
 *              resolved SEO record (see src/api/seo.js) into <head>.
 *
 *              Tags are UPDATED IN PLACE, never appended beside the static ones.
 *              index.html ships every managed tag with a `data-seo="<id>"`
 *              marker (the no-JS crawler fallback, and the placeholders the
 *              backend's edge HTML injector rewrites); this module finds the
 *              tag by that marker, updates its content/href, creates it when it
 *              is missing and REMOVES it when the record's value is null — a
 *              null means "emit no tag", never `content=""`. That is why
 *              react-helmet-async is not used: it appends its own tags next to
 *              the static ones and leaves two <meta name="description">.
 *
 *              Analytics (GA4, GTM, Meta Pixel) are injected at most once per
 *              page life — a client-side navigation must never load a second
 *              copy of a tag manager.
 *
 *              BYTE-IDENTICAL in the neon and beyond builds.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */

// id → how the tag is spelled in <head>. `attr` is the attribute that names a
// <meta>; canonical is the one <link>.
const MANAGED = [
  { id: 'description', attr: 'name', pick: (r) => r.description },
  { id: 'keywords', attr: 'name', pick: (r) => r.keywords },
  { id: 'robots', attr: 'name', pick: (r) => r.robots },
  { id: 'canonical', link: 'canonical', pick: (r) => r.canonical },
  { id: 'og:type', attr: 'property', pick: (r) => r.og?.type },
  { id: 'og:url', attr: 'property', pick: (r) => r.og?.url },
  { id: 'og:title', attr: 'property', pick: (r) => r.og?.title },
  { id: 'og:description', attr: 'property', pick: (r) => r.og?.description },
  { id: 'og:image', attr: 'property', pick: (r) => r.og?.image },
  { id: 'og:site_name', attr: 'property', pick: (r) => r.og?.siteName },
  { id: 'og:locale', attr: 'property', pick: (r) => r.og?.locale },
  { id: 'twitter:card', attr: 'name', pick: (r) => r.twitter?.card },
  { id: 'twitter:site', attr: 'name', pick: (r) => r.twitter?.site },
  { id: 'twitter:title', attr: 'name', pick: (r) => r.twitter?.title },
  { id: 'twitter:description', attr: 'name', pick: (r) => r.twitter?.description },
  { id: 'twitter:image', attr: 'name', pick: (r) => r.twitter?.image },
  { id: 'google-site-verification', attr: 'name', pick: (r) => r.verification?.google },
  { id: 'msvalidate.01', attr: 'name', pick: (r) => r.verification?.bing },
  { id: 'fb:app_id', attr: 'property', pick: (r) => r.facebookAppId },
];

const BY_ID = Object.fromEntries(MANAGED.map((m) => [m.id, m]));

/** A value worth emitting: a non-empty string (numbers are stringified). */
function present(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function find(id) {
  return document.head.querySelector(`[data-seo="${id}"]`);
}

/**
 * Write one managed tag. null/empty REMOVES the tag — a missing value is
 * expressed by the tag's absence, never by an empty content attribute.
 */
export function setTag(id, value) {
  const spec = BY_ID[id];
  if (!spec || typeof document === 'undefined') return;
  const v = present(value);
  let el = find(id);
  if (v == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    if (spec.link) {
      el = document.createElement('link');
      el.setAttribute('rel', spec.link);
    } else {
      el = document.createElement('meta');
      el.setAttribute(spec.attr, spec.id);
    }
    el.setAttribute('data-seo', id);
    document.head.appendChild(el);
  }
  el.setAttribute(spec.link ? 'href' : 'content', v);
}

/** Replace every JSON-LD block with the record's. `<` is escaped so a value can never close the script. */
export function setJsonLd(blocks) {
  if (typeof document === 'undefined') return;
  document.head.querySelectorAll('script[data-seo="jsonld"]').forEach((s) => s.remove());
  const list = Array.isArray(blocks) ? blocks : [];
  for (const block of list) {
    if (!block || typeof block !== 'object') continue;
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-seo', 'jsonld');
    s.textContent = JSON.stringify(block).replace(/</g, '\\u003c');
    document.head.appendChild(s);
  }
}

/** Apply a whole resolved SEO record to the document. */
export function applySeo(r) {
  if (!r || typeof document === 'undefined') return;
  const title = present(r.title);
  if (title) document.title = title;
  const lang = present(r.lang);
  if (lang) document.documentElement.setAttribute('lang', lang);
  for (const spec of MANAGED) setTag(spec.id, spec.pick(r));
  setJsonLd(r.jsonLd);
}

// ── analytics: each loaded at most once per page life ─────────────────────
const loaded = { ga: null, gtm: null, pixel: null };

function addScript(src) {
  const s = document.createElement('script');
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
  return s;
}

/** GA4 (gtag.js). Ignores anything that is not a measurement id. */
export function loadGa(id) {
  const v = present(id);
  if (!v || loaded.ga || typeof window === 'undefined') return false;
  if (!/^G-[A-Z0-9]+$/i.test(v)) return false;
  loaded.ga = v;
  window.dataLayer = window.dataLayer || [];
  // gtag must push the `arguments` object itself, not an array copy.
  // eslint-disable-next-line prefer-rest-params
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', v);
  addScript(`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(v)}`);
  return true;
}

/** Google Tag Manager container. */
export function loadGtm(id) {
  const v = present(id);
  if (!v || loaded.gtm || typeof window === 'undefined') return false;
  if (!/^GTM-[A-Z0-9]+$/i.test(v)) return false;
  loaded.gtm = v;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
  addScript(`https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(v)}`);
  return true;
}

/** Meta (Facebook) Pixel. */
export function loadFacebookPixel(id) {
  const v = present(id);
  if (!v || loaded.pixel || typeof window === 'undefined') return false;
  if (!/^\d+$/.test(v)) return false;
  loaded.pixel = v;
  if (!window.fbq) {
    const fbq = function fbq() {
      // eslint-disable-next-line prefer-rest-params
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments); else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    addScript('https://connect.facebook.net/en_US/fbevents.js');
  }
  window.fbq('init', v);
  window.fbq('track', 'PageView');
  return true;
}
